import type { MouseEvent, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import {
  ContextMenu,
  type ContextMenuItem,
  type DataTableRowReorder,
  ListDetailPane,
  PageHeader,
  PageShell,
  SortableItem,
  SortableList,
  SwatchAddCard,
} from '@core/components';
import { useMenu } from '@core/hooks/useMenu';

type Group = { id: number; name: string };

export function useSelectedGroup<TGroup extends Group>(
  groups: TGroup[],
  param: string
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = Number(searchParams.get(param));
  const selectedGroup =
    groups.find((group) => group.id === selectedId) ?? groups[0] ?? null;

  const selectGroup = (id: number) => {
    setSearchParams({ [param]: String(id) }, { replace: true });
  };

  return { selectedGroup, selectGroup };
}

export interface GroupedSortableSettingsProps<
  TGroup extends Group,
  TItem extends { id: number },
> {
  title: string;
  loading: boolean;
  groups: TGroup[];
  selectedGroup: TGroup | null;
  onSelectGroup: (id: number) => void;
  groupReorder?: DataTableRowReorder;
  groupLabel: string;
  renderGroupSecondary?: (group: TGroup) => ReactNode;
  getGroupMenuItems?: (group: TGroup) => ContextMenuItem[];
  addGroup?: { label: string; onClick: () => void };
  emptyState: { title?: string; description: string };
  getItems: (group: TGroup) => TItem[];
  getItemsCaption: (group: TGroup, count: number) => string;
  onItemsReorder: (group: TGroup, next: TItem[]) => void;
  leadingItem?: ReactNode;
  renderItem: (
    item: TItem,
    onMenuClick: (event: MouseEvent<HTMLElement>) => void
  ) => ReactNode;
  getItemMenuItems: (item: TItem) => ContextMenuItem[];
  addItem?: { label: string; onClick: () => void };
  children?: ReactNode;
}

function GroupedSortableSettings<
  TGroup extends Group,
  TItem extends { id: number },
>({
  title,
  loading,
  groups,
  selectedGroup,
  onSelectGroup,
  groupReorder,
  groupLabel,
  renderGroupSecondary,
  getGroupMenuItems,
  addGroup,
  emptyState,
  getItems,
  getItemsCaption,
  onItemsReorder,
  leadingItem,
  renderItem,
  getItemMenuItems,
  addItem,
  children,
}: GroupedSortableSettingsProps<TGroup, TItem>) {
  const menu = useMenu<ContextMenuItem[]>();

  const items = selectedGroup ? getItems(selectedGroup) : [];
  const itemGrid = selectedGroup ? (
    <>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {selectedGroup.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {getItemsCaption(selectedGroup, items.length)}
        </Typography>
      </Box>
      <SortableList
        items={items}
        onReorder={(next) => onItemsReorder(selectedGroup, next)}
        layout="grid"
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: 1.5,
          }}
        >
          {leadingItem}
          {items.map((item) => (
            <SortableItem key={item.id} id={item.id}>
              {renderItem(item, (event) =>
                menu.openMenu(event, getItemMenuItems(item))
              )}
            </SortableItem>
          ))}
          {addItem ? (
            <SwatchAddCard label={addItem.label} onClick={addItem.onClick} />
          ) : null}
        </Box>
      </SortableList>
    </>
  ) : null;

  return (
    <PageShell header={<PageHeader title={title} />}>
      <ListDetailPane<TGroup>
        items={groups}
        selected={selectedGroup}
        onSelect={onSelectGroup}
        selectLabel={groupLabel}
        renderSecondary={renderGroupSecondary}
        getMenuItems={getGroupMenuItems}
        add={addGroup}
        onReorder={
          groupReorder
            ? (next) =>
                groupReorder.onReorder(next.map((group) => String(group.id)))
            : undefined
        }
        loading={loading}
        emptyState={emptyState}
      >
        {itemGrid}
      </ListDetailPane>
      <ContextMenu
        open={menu.isOpen}
        anchorEl={menu.anchorEl}
        items={menu.item ?? []}
        onClose={menu.closeMenu}
      />
      {children}
    </PageShell>
  );
}

export default GroupedSortableSettings;
